import styled from 'styled-components';
import { ToolbarButton } from '../../../ui/common';

// ── Plan dnia na ekranie (U.29) ───────────────────────────────────────────────────────────────

export const PlanBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

// Trzy sekcje rezerwacji. Obok kalendarza (szeroki ekran) przewijają się w panelu, żeby siatka
// miesiąca nie uciekała z ekranu; pod kalendarzem (tablet, telefon) rosną razem ze stroną, bo
// przewijanie w przewijaniu na dotyku łapie nie ten palec, którego trzeba. „Do przygotowania"
// stoi POZA tym pojemnikiem: długa lista typów nie może zepchnąć kart pod krawędź przewijania.
export const PlanSections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;

  @media (min-width: 1200px) {
    max-height: 480px;
    overflow-y: auto;
    padding-right: 6px;

    &::-webkit-scrollbar { width: 8px; }
    &::-webkit-scrollbar-thumb { background: var(--border); border-radius: 8px; }
    &::-webkit-scrollbar-track { background: transparent; }
  }
`;

// Wspólny przycisk paska (ui/common.js) z miejscem w nagłówku dnia: w wąskim panelu schodzi
// pod datę i zostaje przy prawej krawędzi.
export const PrintButton = styled(ToolbarButton)`
  flex: 0 0 auto;
  margin-left: auto;
`;

export const PrepareBox = styled.section`
  padding: 14px 16px;
  border: 1px solid var(--input-border);
  border-radius: 12px;
  background: var(--hover-bg);
`;

export const PlanHeading = styled.h3`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 10px;
  font-size: 0.86rem;
  font-weight: 700;
  color: var(--text);
`;

export const PlanCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${(p) => (p.$empty ? 'transparent' : 'var(--primary)')};
  border: 1px solid ${(p) => (p.$empty ? 'var(--input-border)' : 'var(--primary)')};
  color: ${(p) => (p.$empty ? 'var(--text-muted)' : 'var(--primary-contrast)')};
  font-size: 0.76rem;
  font-weight: 700;
  line-height: 1.3;
`;

export const PlanHeadingNote = styled.span`
  margin-left: auto;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-muted);
`;

export const PrepareList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const PrepareRow = styled.li`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const PrepareType = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  font-size: 0.92rem;
  font-weight: 700;
  color: var(--text);

  .type-name {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  .type-count {
    flex-shrink: 0;
    font-variant-numeric: tabular-nums;
  }
`;

export const SizeChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const SizeChip = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
  padding: 2px 9px;
  border: 1px solid var(--input-border);
  border-radius: 999px;
  background: var(--card-bg);
  font-size: 0.8rem;
  color: ${(p) => (p.$muted ? 'var(--text-muted)' : 'var(--text)')};

  b { font-weight: 700; font-variant-numeric: tabular-nums; }
`;

export const PlanNote = styled.p`
  margin: 10px 0 0;
  font-size: 0.8rem;
  color: var(--text-muted);
`;

export const PlanSection = styled.section``;

export const PlanCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const PlanEmpty = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: var(--text-muted);
`;

// N.1: pod kartą wydania, gdy sztuka musi najpierw wrócić od poprzedniego klienta.
export const ReturnsTodayList = styled.ul`
  margin: -4px 0 0;
  padding: 6px 10px;
  list-style: none;
  border: 1px solid var(--warning-border);
  border-radius: 8px;
  background: var(--warning-bg);
  color: var(--warning);
  font-size: 0.8rem;
  font-weight: 600;
  overflow-wrap: anywhere;
`;

// ── Plan dnia na papierze ─────────────────────────────────────────────────────────────────────
// Renderowany w ukrytej ramce (components/print/PrintFrame.js). Jej dokument NIE ma zmiennych
// motywu, więc kolory są tu podane wprost, czarno na białym: plan bywa drukowany mono.

export const PrintDoc = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
  font-size: 10.5pt;
  line-height: 1.35;
  color: #111;

  h1 {
    margin: 0;
    font-size: 17pt;
    font-weight: 700;
  }

  h2 {
    margin: 14pt 0 5pt;
    padding-bottom: 3pt;
    border-bottom: 1pt solid #111;
    font-size: 12pt;
    font-weight: 700;
    break-after: avoid;
    page-break-after: avoid;
  }

  .date {
    margin-top: 2pt;
    font-size: 12pt;
  }

  /* Sama pierwsza litera: capitalize podnosiłoby też nazwę miesiąca („10 Października"). */
  .date::first-letter { text-transform: uppercase; }

  .totals {
    margin-top: 4pt;
    color: #444;
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  td {
    padding: 3pt 6pt 3pt 0;
    border-bottom: 0.5pt solid #bbb;
    vertical-align: top;
  }

  td.type { width: 30%; font-weight: 700; }
  td.name { width: 34%; }
  td.count { width: 10%; font-weight: 700; white-space: nowrap; }

  .entry {
    display: flex;
    gap: 8pt;
    padding: 5pt 0;
    border-bottom: 0.5pt solid #bbb;
    break-inside: avoid;
    page-break-inside: avoid;
  }

  /* Kratka do odhaczenia długopisem. */
  .tick {
    flex: 0 0 auto;
    width: 10pt;
    height: 10pt;
    margin-top: 2pt;
    border: 1pt solid #111;
  }

  .entry-main { min-width: 0; flex: 1 1 auto; }
  .meta { color: #444; }

  ul {
    margin: 2pt 0 0;
    padding: 0 0 0 12pt;
  }

  li { margin: 0; }

  .muted { color: #555; }

  .footer {
    margin-top: 14pt;
    font-size: 8.5pt;
    color: #555;
  }
`;
